import React from 'react';
import StyleConstants from '../../../core/styles.constants';
import type { DeskShapeFeature } from '../models/editor-feature';
import { multiPolygonToPath, type Projection } from '../utils/projection';

/** Selected reads strongest, hovered sits just under it, everything else is a hairline. */
const strokeWidthFor = (selected: boolean, hovered: boolean): number => {
  if (selected) return 1.5;
  if (hovered) return 1.2;
  return 0.5;
};

interface Props {
  readonly shape: DeskShapeFeature;
  readonly projection: Projection;
  readonly selected?: boolean;
  readonly hovered?: boolean;
  /**
   * Overrides the resting colours. The editor leaves these alone; the booking viewer sets
   * them because there the shape has to be told apart from the wall fragments beside it,
   * and nothing there is ever selected to make it stand out.
   */
  readonly fill?: string;
  readonly stroke?: string;
}

/**
 * The furniture drawing under a desk — the only floor-layer feature that is editable.
 *
 * Its rotation is not drawn as a transform, unlike a desk marker's: this shape's vertices
 * are genuinely rotated in the stored coordinates, so the path already reflects the angle.
 * Drawing a transform on top would rotate it twice.
 */
const DeskShape = ({
  shape,
  projection,
  selected = false,
  hovered = false,
  fill = '#E0E0DC',
  stroke = StyleConstants.GREY_COLOR,
}: Props): React.JSX.Element => (
  <path
    d={multiPolygonToPath(shape.coordinates, projection)}
    fill={selected || hovered ? '#D6E7F8' : fill}
    stroke={selected || hovered ? StyleConstants.MEDIUM_BLUE_COLOR : stroke}
    strokeWidth={strokeWidthFor(selected, hovered)}
  />
);

export default React.memo(DeskShape);
